import React from 'react';
import { Alert, Box, Button, Container, Paper, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import colors from '../assets/theme/colors';
import { useChildContext } from '../contexts/ChildContext';
import useChildName from '../hooks/useChildName';
import CareSourceFileUploadButton from '../features/careSourceProcessing/CareSourceFileUploadButton';
import { CARE_SOURCE_CONTEXTS } from '../features/careSourceProcessing/careSourceMetadata';
import { buildFriendlyDocumentTitle, getDocumentOriginSuggestion } from '../features/careSourceProcessing/documentOrganizationModel';
import { saveDocumentOrganization } from '../features/careSourceProcessing/documentOrganizationService';
import ChildShellBottomNav from '../components/Dashboard/shared/ChildShellBottomNav';
import { navigateFromDocumentLibrary } from '../features/careSourceProcessing/documentLibraryNavigation';

const FilingStep = ({ number, title, children }) => (
  <Stack direction="row" spacing={1.2} alignItems="flex-start">
    <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: colors.semantic.sageMist, fontSize: '0.76rem', fontWeight: 900 }}>{number}</Box>
    <Box><Typography sx={{ fontWeight: 850 }}>{title}</Typography><Typography color="text.secondary" sx={{ fontSize: '0.86rem', lineHeight: 1.45 }}>{children}</Typography></Box>
  </Stack>
);

const AddLibraryFilePage = () => {
  const navigate = useNavigate();
  const { currentChildId } = useChildContext();
  const { childName, loading } = useChildName(currentChildId);
  const [savedSource, setSavedSource] = React.useState(null);
  const [displayTitle, setDisplayTitle] = React.useState('');
  const [originName, setOriginName] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  const finishOrganization = async (confirmedOriginName = originName) => {
    setSaving(true);
    setError('');
    try {
      await saveDocumentOrganization({ sourceNoteId: savedSource.sourceNoteId, displayTitle, confirmedOriginName });
      navigate('/templates?tab=documents');
    } catch (saveError) {
      setError(saveError?.message || 'We could not save this document label.');
    } finally {
      setSaving(false);
    }
  };

  const handleNavigation = (itemId) => navigateFromDocumentLibrary({ navigate, childId: currentChildId, itemId });

  if (loading) return <Container sx={{ py: 4 }}>Loading…</Container>;

  return <Box sx={{ minHeight: '100vh', bgcolor: colors.semantic.page, py: { xs: 2, md: 4 } }}>
    <Container maxWidth="lg">
      <Button onClick={() => navigate('/templates?tab=documents')} sx={{ px: 0, mb: 1, textTransform: 'none' }}>← Library</Button>
      <Typography component="h1" sx={{ fontSize: { xs: '1.55rem', md: '1.85rem' }, fontWeight: 940 }}>Add a file</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.3, mb: 2.2 }}>Add a document or image to {childName ? `${childName}’s` : 'the'} Library. The original always stays linked.</Typography>
      {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}
      {!savedSource ? <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.2fr) minmax(320px, 0.8fr)' }, gap: 2 }}>
        <CareSourceFileUploadButton
          childId={currentChildId || ''}
          sourceType="file_import"
          sourceContext={CARE_SOURCE_CONTEXTS.DOCUMENT}
          label="Choose a file"
          helperText="PDF, DOCX, TXT, CSV, XLSX, PNG or JPG. Up to 20 MB. You can also drop one file here."
          acceptedFiles=".pdf,.txt,.csv,.tsv,.xls,.xlsx,.docx,.png,.jpg,.jpeg,image/png,image/jpeg"
          dropZone
          onSaved={(result, context) => {
            const title = buildFriendlyDocumentTitle(context?.file?.name || 'Care document');
            setDisplayTitle(title);
            setOriginName(getDocumentOriginSuggestion(result));
            setSavedSource(result);
          }}
          buttonSx={{ bgcolor: colors.semantic.sageDeep, color: '#fff', borderColor: colors.semantic.sageDeep, '&:hover': { bgcolor: colors.semantic.sageDeep, color: '#fff' } }}
        />
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
          <Typography component="h2" sx={{ mb: 1.8, fontWeight: 900 }}>What happens next</Typography>
          <Stack spacing={1.8}>
            <FilingStep number="1" title="We preserve the original">The file is saved as a source before anything is organized.</FilingStep>
            <FilingStep number="2" title="Lifelog reads it">Documents use the current extraction pipeline. PNG and JPG images use the current OCR path.</FilingStep>
            <FilingStep number="3" title="You review what was found">Dated care details can become linked events. Uncertain details remain visible for review.</FilingStep>
          </Stack>
        </Paper>
      </Box> : <Paper variant="outlined" sx={{ maxWidth: 620, p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Typography component="h2" sx={{ fontSize: '1.2rem', fontWeight: 900 }}>Make this easy to find</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.4, mb: 2 }}>The original is saved. Confirm a clear name and where it came from.</Typography>
        <Stack spacing={1.7}>
          <TextField label="Document name" value={displayTitle} onChange={(event) => setDisplayTitle(event.target.value)} helperText="The original filename stays unchanged." />
          <TextField label="Where did this come from?" value={originName} onChange={(event) => setOriginName(event.target.value)} placeholder="Person or organization, such as Dr. Patel" helperText="Use a name you will recognize later." />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" disabled={saving || !displayTitle.trim() || !originName.trim()} onClick={() => finishOrganization()} sx={{ textTransform: 'none', fontWeight: 850 }}>Confirm and open Library</Button>
            <Button disabled={saving || !displayTitle.trim()} onClick={() => finishOrganization('')} sx={{ textTransform: 'none', fontWeight: 800 }}>I’m not sure</Button>
          </Stack>
        </Stack>
      </Paper>}
    </Container>
    <Box sx={{ display: { xs: 'block', md: 'none' } }}><ChildShellBottomNav activeItem="" onSelect={handleNavigation} /></Box>
  </Box>;
};

export default AddLibraryFilePage;
